import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Mail } from 'lucide-react';
import { AnimatedSection, StaggerChildren, StaggerItem } from '@/components/AnimatedComponents';
import VideoPlayer from '@/components/VideoPlayer';
import VideoCarousel from '@/components/VideoCarousel';

const projects = [
  {
    id: 1,
    title: 'Meet Terry Webster',
    client: 'Introduction',
    category: 'Broadcast',
    year: '2025',
    description: 'Get to know the filmmaker behind the camera. With 17 years of experience in broadcast, online content, and commercial production, Terry brings a unique blend of technical expertise and creative storytelling to every project.',
    image: 'https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/npmy625n_Terry_homepage_photo_01.jpg',
    vimeoId: '681086047',
    services: ['Broadcast Production', 'Online Content', 'Commercial Filmmaking'],
    isIntro: true,
  },
  {
    id: 2,
    title: 'Home Beautiful Web Series',
    client: 'Gocollaborate',
    category: 'Online',
    year: '2024',
    description: 'Working alongside Gocollaborate\'s talented team of production and post-production experts to deliver three web series for Home Beautiful. An incredible collaboration that pushed creative boundaries — I learnt so much from these amazing people while bringing the brand\'s vision to life.',
    image: 'https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/hu0z3s6r_Terry_commercial_photo_03.jpg',
    vimeoId: '467136086',
    videos: [
      { id: '467136086', title: 'Beach House', thumbnail: 'https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/hu0z3s6r_Terry_commercial_photo_03.jpg' },
      { id: '1048772073', title: 'Cooks Co-Op', thumbnail: 'https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/elf06n2p_thumb%20%281%29.jpg' },
      { id: '219255415', title: 'Family Home', thumbnail: 'https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/x18w9sdw_Terry_commercial_photo_04.jpg' },
    ],
    services: ['Production', 'Post Production', 'Web Series'],
  },
  {
    id: 3,
    title: 'Man Eats Wild',
    client: 'Outdoor Channel',
    category: 'Broadcast',
    year: '2023',
    description: 'Proud to have been involved from the very beginning — helping bring this show to the Outdoor Channel. I shot several episodes of series one and created the initial website and pitch decks that helped sell the concept. A true ground-up production journey.',
    image: 'https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/so2xizkw_Terry_commercial_photo_05.jpg',
    vimeoId: null,
    videos: [
      { id: '364411333', title: 'Episode 1', thumbnail: 'https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/so2xizkw_Terry_commercial_photo_05.jpg' },
      { id: '364419499', title: 'Episode 2', thumbnail: 'https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/a75d7y1j_Terry_commercial_photo_06.jpg' },
      { id: '304622519', title: 'Episode 3', thumbnail: 'https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/4tdqq6zq_Terry_commercial_photo_07.jpg' },
    ],
    services: ['Director', 'Cinematography', 'Pitch Development'],
  },
  {
    id: 4,
    title: 'Husqvarna Launch Series',
    client: 'Husqvarna',
    category: 'Online',
    year: '2022',
    description: 'Brought on by the marketing team to create a launch series with an online competition that encouraged audiences to share and engage. The results were incredible — driving massive reach and brand awareness.',
    image: 'https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/j5r308j8_Terry_commercial_photo_08.jpg',
    vimeoId: '213640001',
    services: ['Production', 'Online Content', 'Meta & Pmax Ads'],
  },
];

const categories = ['All', 'Broadcast', 'Online', 'Meta & Pmax Ads', 'Production', 'Post Production', 'Audio Mastering', 'Colour Grading'];

export default function CommercialWork() {
  return (
    <main data-testid="commercial-work-page">
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-[#050505]">
        <div className="container-wide">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="caption mb-4"
          >
            Portfolio
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-6xl lg:text-8xl font-bold tracking-tighter mb-6"
          >
            Commercial<br />
            <span className="text-[#007AFF]">Work</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-[#A1A1AA] text-lg md:text-xl max-w-2xl"
          >
            High-end brand campaigns and commercial content crafted for 
            leading companies across Australia and beyond.
          </motion.p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-[#0C0C0C] border-y border-[#27272A] sticky top-20 z-40 glass">
        <div className="container-wide">
          <div className="flex items-center gap-4 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category}
                className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  category === 'All' 
                    ? 'bg-[#007AFF] text-white' 
                    : 'text-[#A1A1AA] hover:text-white'
                }`}
                data-testid={`filter-${category.toLowerCase()}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="projects-grid">
        <div className="container-wide">
          <div className="space-y-32">
            {projects.map((project, index) => (
              <AnimatedSection key={project.id}>
                <div 
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  {/* Video/Image */}
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    {project.videos ? (
                      <VideoCarousel 
                        videos={project.videos}
                        posterImage={project.image}
                        title={project.title}
                      />
                    ) : (
                      <VideoPlayer 
                        vimeoId={project.vimeoId}
                        posterImage={project.image}
                        title={project.title}
                        className="rounded-sm"
                      />
                    )}
                  </div>

                  {/* Content */}
                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <div className="flex items-center gap-4 mb-4">
                      <span className="caption">{project.category}</span>
                      <span className="text-[#52525B]">|</span>
                      <span className="text-[#52525B] text-sm">{project.year}</span>
                    </div>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
                      {project.title}
                    </h2>
                    <p className="text-[#A1A1AA] text-lg leading-relaxed mb-6">
                      {project.description}
                    </p>
                    
                    {/* Services */}
                    <div className="mb-8">
                      <p className="text-sm text-[#52525B] mb-3">Services:</p>
                      <div className="flex flex-wrap gap-2">
                        {project.services.map((service) => (
                          <span 
                            key={service}
                            className="px-3 py-1 text-sm border border-[#27272A] text-[#A1A1AA]"
                          >
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>

                    {project.isIntro ? (
                      <Link 
                        to="/contact"
                        className="btn-primary inline-flex items-center gap-2"
                        data-testid={`contact-project-${project.id}`}
                      >
                        <Mail size={16} />
                        Get in Contact
                      </Link>
                    ) : (
                      <button 
                        className="btn-secondary inline-flex items-center gap-2"
                        data-testid={`view-project-${project.id}`}
                      >
                        View Project
                        <ArrowRight size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0C0C0C] border-t border-[#27272A]">
        <div className="container-wide text-center">
          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter mb-6">
              Ready to elevate your brand?
            </h2>
            <p className="text-[#A1A1AA] text-lg max-w-xl mx-auto mb-8">
              Let&apos;s discuss your next commercial project and create something extraordinary together.
            </p>
            <Link 
              to="/contact" 
              className="btn-primary inline-flex items-center gap-2"
              data-testid="contact-cta-btn"
            >
              Start a Project
              <ArrowRight size={18} />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </main>
  );
}
